import api from 'api'
import { digObject } from 'functions/utilities'

export const buyerProfiles = [
  { key: 'BUILDER_RENOVATOR', title: 'Builders/Renovators' },
  { key: 'DEVELOPER', title: 'Developers' },
  { key: 'DOWNSIZER', title: 'Downsizers' },
  { key: 'FAMILY', title: 'Families' },
  { key: 'FIFO_WORKER', title: 'FIFO Workers (Fly in, Fly out)' },
  { key: 'FIRST_HOME_BUYER', title: 'First Home Buyers' },
  { key: 'INTERNATIONAL_BUYER', title: 'International Buyers and Expatriates' },
  { key: 'INVESTOR', title: 'Investors' },
  { key: 'PROFESSIONAL_COUPLE', title: 'Professional Couples' },
  { key: 'REGIONAL_BUYER', title: 'Regional Buyers' },
]

export const timelineSuggestions = [
  { key: '1', title: 'Styling' },
  { key: '2', title: 'Team Visit' },
  { key: '3', title: 'Send Live' },
  { key: '4', title: 'Signboard Install' },
  { key: '5', title: 'First Open Home' },
  { key: '6', title: 'Auction' },
  { key: '7', title: 'Possible Settlement' },
]

export function getBuyerProfileByKey(key){
  return buyerProfiles.find(profile => profile.key === key)
}

export function calculateCampaignDuration(a, b){
  const duration = b.diff(a, 'weeks')
  return `${duration}w`
}

export function combineCampaignEvents(campaign, events){
  let combinedEvents = { ...events }

  if (campaign.dates && campaign.dates.auction.date){
    const auction = {
      id: 'auction',
      locked: true,
      title: 'Auction Date',
      dates: {
        date: {
          date: campaign.dates.auction.date,
        },
      },
    }

    combinedEvents = {
      ...combinedEvents,
      auction,
    }
  }

  return combinedEvents
}

export function addressWithSuburb(campaign){
  const { address, suburb_name } = campaign
  return `${address}, ${suburb_name}`
}

export function getFullAddress(campaign){
  const {
    title, unit_number, street_number, street_name, suburb_name, state_name, post_code,
  } = campaign

  const unitWithStreet = unit_number ? `${unit_number}/${street_number}` : street_number
  const street = title || `${unitWithStreet} ${street_name}, ${suburb_name}`

  return `${street}, ${state_name} ${post_code}`
}

export function getStreetAddress(campaign){
  const { unit_number, street_number, street_name } = campaign

  const number = unit_number ? `${unit_number}/${street_number}` : street_number

  return `${number} ${street_name}`
}

export function getCampaignLinkByPermissions(campaign, permissions){
  const { id } = campaign
  const link = `/agency/campaigns/${id}/edit`

  if (digObject(permissions, 'campaign.orders.manage')){
    return link
  }

  return `${link}#/quotes`
}

export function searchCampaigns(string, requestOptions, options = {}){
  const { url } = options || {}
  const requestUrl = url || '/agency/campaigns/search.json'

  const mergedOptions = {
    string,
    limit: 10,
    hide_hidden: true,
    ...requestOptions,
  }

  return api(requestUrl, mergedOptions)
    .then(response => response.json().then(json => ({ json, response })))
    .then(({ json, response }) => {
      if (!response.ok){
        return Promise.reject(json)
      }

      return { success: true, result: json }
    })
    .catch(err => ({ success: false, result: err }))
}

export function splitFeatureString(featureString){
  if (featureString && featureString.length){
    return featureString.split('\n').filter(string => string)
  }

  return []
}

export function missingCampaignFieldsForProduct(campaign, serviceProduct){
  const requiredCampaignFields = digObject(serviceProduct, 'options.required_campaign_fields', [])

  const missingFields = requiredCampaignFields.length && campaign.id ? requiredCampaignFields.filter(field => !campaign[field]) : []

  return missingFields
}

export function combineAgentsAndTeamMembers(agentUsers, teamMembers){
  const combined = []
  const agentUserIds = agentUsers.map(u => u.id)
  const members = teamMembers.filter(member => !agentUserIds.includes(member.user_id))

  agentUsers.map((user, index) => {
    const teamMember = teamMembers.find(member => member.user_id === user.id) || {}
    const member_role = index === 0 ? 'lead_agent' : 'associate_agent'
    const member = teamMember.id ? { ...teamMember, member_role } : { member_role, user_id: user.id }

    combined.push(member)
  })

  return combined.concat(members)
}

export const campaignFields = [
  { title: 'Auction Date', key: 'auction_date', inputType: 'datetime' },
  { title: 'Bathrooms', key: 'bathrooms', inputType: 'text' },
  { title: 'Bedrooms', key: 'bedrooms', inputType: 'text' },
  { title: 'Campaign Duration', key: 'campaign_duration', inputType: 'text' },
  { title: 'Car Spaces', key: 'car_spaces', inputType: 'number' },
  { title: 'Carparks', key: 'carparks', inputType: 'text' },
  { title: 'Carports', key: 'carports', inputType: 'text' },
  { title: 'Council Rates', key: 'council_rates', inputType: 'text' },
  { title: 'Country Code', key: 'country_code', inputType: 'text' },
  { title: 'Country Name', key: 'country_name', inputType: 'text' },
  { title: 'Start Date', key: 'start_date', inputType: 'date' },
  { title: 'Energy Rating', key: 'energy_rating', inputType: 'text' },
  { title: 'Facilities', key: 'facilities', inputType: 'number' },
  { title: 'Frontage', key: 'frontage', inputType: 'number' },
  { title: 'Price High', key: 'highest_search', inputType: 'number' },
  { title: 'Inspection Details', key: 'inspection_details', inputType: 'text' },
  { title: 'Land Area', key: 'land_area', inputType: 'number' },
  { title: 'Land Area Units', key: 'land_area_units', inputType: 'text' },
  { title: 'Land Details', key: 'land_details', inputType: 'text' },
  { title: 'Land Zoning', key: 'land_zoning', inputType: 'text' },
  { title: 'Latitude', key: 'latitude', inputType: 'number' },
  { title: 'Legal Description', key: 'legal_description', inputType: 'text' },
  { title: 'Living Areas', key: 'living_areas', inputType: 'text' },
  { title: 'Longitude', key: 'longitude', inputType: 'number' },
  { title: 'Lot Number', key: 'lot_number', inputType: 'text' },
  { title: 'Price Low', key: 'lowest_search', inputType: 'number' },
  { title: 'Municipality', key: 'municipality', inputType: 'text' },
  { title: 'Post Code', key: 'post_code', inputType: 'text' },
  { title: 'Price', key: 'price', inputType: 'number' },
  { title: 'Price Guide', key: 'price_guide', inputType: 'text' },
  { title: 'Property Url', key: 'property_url', inputType: 'text' },
  { title: 'Published', key: 'published', inputType: 'boolean' },
  { title: 'Rental Bond', key: 'rental_bond', inputType: 'number' },
  { title: 'Rental Period', key: 'rental_period', inputType: 'text' },
  { title: 'Site Name', key: 'site_name', inputType: 'text' },
  { title: 'Sold', key: 'sold', inputType: 'boolean' },
  { title: 'Sold Price', key: 'sold_price', inputType: 'number' },
  { title: 'Start Date', key: 'start_date', inputType: 'date' },
  { title: 'State Name', key: 'state_name', inputType: 'date' },
  { title: 'Street Name', key: 'street_name', inputType: 'text' },
  { title: 'Street Number', key: 'street_number', inputType: 'text' },
  { title: 'Studies', key: 'studies', inputType: 'text' },
  { title: 'Suburb Name', key: 'suburb_name', inputType: 'text' },
  { title: 'Title', key: 'title', inputType: 'text' },
  { title: 'Under Offer', key: 'under_offer', inputType: 'boolean' },
  { title: 'Unit Number', key: 'unit_number', inputType: 'text' },
  { title: 'Year Built', key: 'year_built', inputType: 'text' },
]

export default buyerProfiles
